import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Auto-refresh detector — pollss `/version.json` (written by vite's
 * `appVersionPlugin` at build time) and compares the server's current
 * `buildId` with the one baked into this bundle at compile time. When
 * they differ, a new deploy has happened *after* the user's tab loaded
 * and the running JS bundle is stale.
 *
 * Lets us deprecate the "hard refresh after every deploy" ask in favor
 * of an automatic banner. Operators see "A new version is available"
 * within ~3 minutes of any deploy and can refresh on their own
 * schedule (no auto-reload — they might be mid-edit).
 *
 * Polling pauses while the tab is in the background. Visibility-change
 * triggers an immediate re-check so a user returning from another tab
 * sees the banner without waiting up to 3 minutes.
 *
 * Errors (network blips, 404s during a deploy window) are swallowed —
 * we never want the version checker itself to surface a user-facing
 * error. The next interval will retry.
 */

// Tight poll interval — the banner needs to surface within seconds of a
// deploy, not minutes. Combined with the per-route triggerCheck below,
// most operators will see the banner before they finish their next
// click in the app.
const CHECK_INTERVAL_MS = 60 * 1000; // 60 seconds

// Throttle to avoid hammering /version.json on rapid route changes
// (e.g. wizard-style flows that change `location` multiple times per
// second). 5s is plenty given the file is ~64 bytes.
const MIN_CHECK_GAP_MS = 5 * 1000;

export interface AppVersionState {
  newVersionAvailable: boolean;
  refresh: () => void;
  /** Force an immediate /version.json fetch (e.g. on route change). */
  triggerCheck: () => void;
}

export function useAppVersionCheck(): AppVersionState {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  // Track the build id we loaded with so setState doesn't flap if a
  // mid-cycle deploy re-deploys the same id.
  const loadedBuildId = useRef(__BUILD_ID__);
  const lastCheckAt = useRef(0);
  const cancelledRef = useRef(false);

  const check = useCallback(async (force = false) => {
    if (cancelledRef.current) return;
    if (!force && Date.now() - lastCheckAt.current < MIN_CHECK_GAP_MS) return;
    lastCheckAt.current = Date.now();
    try {
      const res = await fetch('/version.json', {
        cache: 'no-cache',
        credentials: 'omit',
      });
      if (!res.ok) return;
      const data = (await res.json()) as { buildId?: string };
      const serverBuildId = data?.buildId;
      if (!serverBuildId) return;
      if (serverBuildId !== loadedBuildId.current) {
        setNewVersionAvailable(true);
      }
    } catch {
      // Swallow — brief 502 during deploy or offline tab. Next call retries.
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    void check(true);

    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') void check();
    }, CHECK_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') void check(true);
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelledRef.current = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [check]);

  return {
    newVersionAvailable,
    refresh: () => {
      // index.html is served no-cache by nginx; a normal reload picks
      // up the new bundle hash references.
      window.location.reload();
    },
    triggerCheck: () => {
      void check();
    },
  };
}
